import React from 'react';
import { Document, Text, StyleSheet } from '@react-pdf/renderer';
import type { HandoverPreviewRow, HandoverRowResult } from '../../../services/admissions/handoverService';
import {
  Page, Section, DataTable, StatStrip, type DataTableColumn,
  pdfColors, pdfFontSize,
  fmtClassName,
  type SchoolBranding, type DocumentMeta,
} from '../../../utils/pdf';

interface Props {
  results: HandoverRowResult[];
  /** The preview rows that were run — supplies each applicant's name and
   *  class, which the batch result itself does not carry. */
  previewRows?: HandoverPreviewRow[];
  cycleName?: string;
  branding: SchoolBranding;
  meta: DocumentMeta;
}

const styles = StyleSheet.create({
  name: { fontSize: pdfFontSize.sm, fontWeight: 600, color: pdfColors.textPrimary },
  sub: { fontSize: pdfFontSize.xxs, color: pdfColors.textMuted, marginTop: 1 },
});

/** HandoverSummaryDocument — post-batch report: outcome tallies, then who
 *  was enrolled (with their new GR and roll numbers) and why any row was
 *  skipped or failed. */
const HandoverSummaryDocument: React.FC<Props> = ({ results, previewRows, cycleName, branding, meta }) => {
  const enrolled = results.filter((r) => r.outcome === 'enrolled');
  const skipped = results.filter((r) => r.outcome === 'skipped');
  const failed = results.filter((r) => r.outcome === 'failed');

  const byId = new Map((previewRows ?? []).map((p) => [p.applicationId, p]));
  const pct = (n: number) => (results.length ? `${Math.round((n / results.length) * 100)}% of batch` : undefined);

  const applicant: DataTableColumn<HandoverRowResult> = {
    key: 'applicant', header: 'Applicant', flex: 2,
    render: (r) => {
      const p = byId.get(r.applicationId);
      const cls = p?.class && typeof p.class === 'object' ? fmtClassName(p.class.name) : '';
      return (
        <>
          <Text style={styles.name}>{p?.name || `Application ${r.applicationId.slice(-6)}`}</Text>
          {cls ? <Text style={styles.sub}>{cls}</Text> : null}
        </>
      );
    },
  };
  const index: DataTableColumn<HandoverRowResult> = {
    key: 'idx', header: '#', width: 30, align: 'center', render: (_r, i) => String(i + 1),
  };

  const enrolledCols: Array<DataTableColumn<HandoverRowResult>> = [
    index,
    applicant,
    { key: 'gr', header: 'GR number', width: 110, mono: true, render: (r) => r.grNumber || '—' },
    { key: 'roll', header: 'Roll number', width: 90, align: 'right',
      render: (r) => (r.rollNumber != null ? String(r.rollNumber) : '—') },
  ];
  const noteCols: Array<DataTableColumn<HandoverRowResult>> = [
    index,
    applicant,
    { key: 'reason', header: 'Reason', flex: 3, render: (r) => r.reason || '—' },
  ];

  const metaSubtitled: DocumentMeta = { ...meta, subtitle: meta.subtitle || cycleName };

  return (
    <Document title={meta.title} author={branding.name}>
      <Page
        branding={branding}
        meta={metaSubtitled}
        official={{ details: [...(metaSubtitled.subtitle ? [{ value: metaSubtitled.subtitle }] : []), { value: `${results.length} processed` }] }}
      >
        <StatStrip
          items={[
            { label: 'Processed', value: results.length },
            { label: 'Enrolled', value: enrolled.length, tone: 'success', caption: pct(enrolled.length) },
            { label: 'Skipped', value: skipped.length, tone: 'info', caption: 'Already enrolled or not ready' },
            { label: 'Failed', value: failed.length, tone: failed.length ? 'danger' : undefined, caption: failed.length ? 'Review before re-running' : undefined },
          ]}
        />

        {enrolled.length > 0 && (
          <Section index={1} title="Enrolled" caption={`${enrolled.length} student${enrolled.length === 1 ? '' : 's'}`}>
            <DataTable columns={enrolledCols} rows={enrolled} compact zebra />
          </Section>
        )}
        {failed.length > 0 && (
          <Section index={2} title="Failed" caption={`${failed.length} row${failed.length === 1 ? '' : 's'}`}>
            <DataTable columns={noteCols} rows={failed} compact zebra />
          </Section>
        )}
        {skipped.length > 0 && (
          <Section index={failed.length > 0 ? 3 : 2} title="Skipped" caption={`${skipped.length} row${skipped.length === 1 ? '' : 's'}`}>
            <DataTable columns={noteCols} rows={skipped} compact zebra />
          </Section>
        )}
      </Page>
    </Document>
  );
};

export default HandoverSummaryDocument;
